import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, LogOut, LayoutGrid, ListChecks, Heart, User, Check, ArrowUpRight, Bookmark,
} from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { accountGet, accountSend } from "@/lib/api";
import { ETAPAS_DATA, ETAPAS_ORDER } from "@/data/etapasData";

const ETAPAS = ["Planejamento", "Gestação", "Parto", "Pós-parto"];

const TABS = [
  { id: "feed", label: "Para você", icon: LayoutGrid },
  { id: "checklists", label: "Meus checklists", icon: ListChecks },
  { id: "favoritos", label: "Salvos", icon: Heart },
  { id: "perfil", label: "Meu perfil", icon: User },
];

const CardArtigo = ({ a, right }) => (
  <div className="group relative bg-white rounded-2xl overflow-hidden border border-maternnia-nevoa/60 shadow-sm hover:shadow-md transition-all duration-300">
    <Link to={`/conteudos/${a.slug}`} className="block">
      <div className="h-36 overflow-hidden">
        <img src={a.imagem} alt={a.imagemAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-5">
        <span className="text-xs font-semibold tracking-wide uppercase text-maternnia-eucalipto">{a.etapa}</span>
        <h3 className="mt-1.5 font-serif text-lg text-maternnia-charcoal leading-snug font-medium">{a.titulo}</h3>
      </div>
    </Link>
    {right}
  </div>
);

const Feed = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { accountGet("/account/feed").then((d) => setRows(d || [])); }, []);
  if (!rows) return <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-16" />;
  return (
    <div data-testid="conta-feed">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-1">Conteúdos para o seu momento</h2>
      <p className="text-sm text-maternnia-taupe font-light mb-6">Selecionados de acordo com a sua etapa.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.map((a) => <CardArtigo key={a.id} a={a} />)}
      </div>
    </div>
  );
};

const Checklists = ({ user, onProgress }) => {
  const [progress, setProgress] = useState(user.checklist_progress || {});
  const toggle = async (slug, i) => {
    const cur = new Set(progress[slug] || []);
    cur.has(i) ? cur.delete(i) : cur.add(i);
    const arr = [...cur].sort((a, b) => a - b);
    const next = { ...progress, [slug]: arr };
    setProgress(next);
    try { await accountSend(`/account/checklist/${slug}`, "PUT", { checked: arr }); onProgress?.(next); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div data-testid="conta-checklists" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-1">Meus checklists</h2>
        <p className="text-sm text-maternnia-taupe font-light">Seu progresso fica salvo na conta.</p>
      </div>
      {ETAPAS_ORDER.map((slug) => {
        const data = ETAPAS_DATA[slug];
        const checked = new Set(progress[slug] || []);
        const pct = Math.round((checked.size / data.checklist.length) * 100);
        return (
          <div key={slug} data-testid={`checklist-${slug}`} className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-5 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-xl text-maternnia-charcoal font-medium">{data.nome}</h3>
              <span className="text-xs font-medium text-maternnia-eucalipto">{pct}%</span>
            </div>
            <div className="h-1.5 bg-maternnia-porcelana rounded-full mb-4 overflow-hidden">
              <div className="h-full bg-maternnia-eucalipto transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="space-y-1">
              {data.checklist.map((item, i) => (
                <button key={i} data-testid={`chk-${slug}-${i}`} onClick={() => toggle(slug, i)}
                  className="w-full flex items-center gap-3 text-left p-2 rounded-lg hover:bg-maternnia-porcelana/50 transition-colors">
                  <span className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${checked.has(i) ? "bg-maternnia-eucalipto border-transparent" : "border-maternnia-nevoa bg-white"}`}>
                    {checked.has(i) && <Check size={12} className="text-white" />}
                  </span>
                  <span className={`text-sm ${checked.has(i) ? "text-maternnia-taupe/70 line-through" : "text-maternnia-charcoal"}`}>{item}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Favoritos = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { accountGet("/account/favorites").then((d) => setRows(d || [])); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (slug) => {
    try { await accountSend("/account/favorites", "POST", { slug }); setRows((r) => r.filter((x) => x.slug !== slug)); toast.success("Removido dos salvos."); }
    catch (e) { toast.error(e.message); }
  };
  if (!rows) return <Loader2 className="animate-spin text-maternnia-eucalipto mx-auto mt-16" />;
  return (
    <div data-testid="conta-favoritos">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-6">Conteúdos salvos</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-maternnia-taupe font-light">Você ainda não salvou nenhum conteúdo. Toque no marcador nos artigos para salvar aqui.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((a) => (
            <CardArtigo key={a.id} a={a} right={
              <button data-testid={`fav-remove-${a.slug}`} onClick={() => remove(a.slug)} className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center text-maternnia-pink shadow-sm hover:bg-white">
                <Heart size={16} className="fill-maternnia-pink" />
              </button>
            } />
          ))}
        </div>
      )}
    </div>
  );
};

const Perfil = ({ user, onSaved }) => {
  const [form, setForm] = useState({ nome: user.nome || "", etapa: user.etapa || "", cidade: user.cidade || "" });
  const [busy, setBusy] = useState(false);
  const inp = "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto";
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const u = await accountSend("/account/profile", "PATCH", form); onSaved?.(u); toast.success("Perfil atualizado."); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="conta-perfil" className="max-w-md">
      <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium mb-6">Meu perfil</h2>
      <form onSubmit={save} className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-6 space-y-4">
        <div><label className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Nome</label>
          <input data-testid="perfil-nome" className={inp} value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} /></div>
        <div><label className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Etapa atual</label>
          <select data-testid="perfil-etapa" className={inp} value={form.etapa} onChange={(e) => setForm((f) => ({ ...f, etapa: e.target.value }))}>
            <option value="">Selecione</option>{ETAPAS.map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <div><label className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Cidade</label>
          <input data-testid="perfil-cidade" className={inp} value={form.cidade} onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))} /></div>
        <p className="text-xs text-maternnia-taupe font-light">E-mail: {user.email}</p>
        <button data-testid="perfil-salvar" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-maternnia-floresta transition disabled:opacity-70">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Salvar alterações"}
        </button>
      </form>
    </div>
  );
};

const MinhaContaPage = () => {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/entrar", { replace: true });
    else if (user.role === "fornecedor") navigate("/painel-fornecedor", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user || user.role === "fornecedor") {
    return <main className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-maternnia-eucalipto" /></main>;
  }

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };

  return (
    <main data-testid="minha-conta-page" className="bg-maternnia-porcelana/30 min-h-screen">
      <Seo title="Minha conta" path="/minha-conta" noindex />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium">Olá, {user.nome?.split(" ")[0] || "tudo bem"}!</h1>
            <p className="text-sm text-maternnia-taupe font-light mt-1">{user.etapa ? `Etapa atual: ${user.etapa}` : "Complete seu perfil para personalizar sua jornada."}</p>
          </div>
          <button data-testid="conta-logout" onClick={doLogout} className="inline-flex items-center gap-2 text-sm font-medium text-maternnia-taupe hover:text-maternnia-charcoal border border-maternnia-nevoa rounded-full px-4 py-2 transition">
            <LogOut size={15} /> Sair
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`conta-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 text-sm font-medium rounded-full px-4 py-2.5 transition-all ${tab === t.id ? "bg-maternnia-charcoal text-white" : "bg-white text-maternnia-taupe hover:bg-maternnia-nevoa/40"}`}>
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "feed" && <Feed />}
        {tab === "checklists" && <Checklists user={user} onProgress={(p) => setUser({ ...user, checklist_progress: p })} />}
        {tab === "favoritos" && <Favoritos />}
        {tab === "perfil" && <Perfil user={user} onSaved={(u) => setUser(u)} />}
      </div>
    </main>
  );
};

export default MinhaContaPage;
